import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { AppDrawer } from '../components/ui/AppDrawer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '../components/ui/table';
import { SortableTableHead } from '../components/ui/SortableTableHead';
import { toast } from 'sonner';
import { AutomationsDrawer } from './PipelineAutomations';
import api from '../lib/api';
import {
  Plus, Search, Settings2, Archive, Trash2, RotateCcw, AlertTriangle,
  ChevronUp, ChevronDown, Clock, User as UserIcon,
  ArrowRight, Loader2, X, AlertCircle, LayoutGrid, List,
  SlidersHorizontal, DollarSign, TrendingUp, Target, Timer,
  FileText, ExternalLink, CheckCircle2, BarChart3, Users, Zap,
  MoreVertical,
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { MobileFAB } from '../components/MobileFAB';

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------
const pipelineAPI = {
  getBoard: (params) => api.get('/pipeline/board', { params }),
  getStages: (params) => api.get('/pipeline/stages', { params }),
  getStats: () => api.get('/pipeline/stats'),
  createStage: (d) => api.post('/pipeline/stages', d),
  updateStage: (id, d) => api.put(`/pipeline/stages/${id}`, d),
  reorderStages: (d) => api.post('/pipeline/stages/reorder', d),
  getStages: (params) => api.get('/pipeline/stages', { params }),
  archiveStage: (id) => api.post(`/pipeline/stages/${id}/archive`),
  restoreStage: (id) => api.post(`/pipeline/stages/${id}/restore`),
  deleteStage: (id) => api.delete(`/pipeline/stages/${id}`),
  createRecord: (d) => api.post('/pipeline/records', d),
  getRecord: (id) => api.get(`/pipeline/records/${id}`),
  updateRecord: (id, d) => api.put(`/pipeline/records/${id}`, d),
  moveRecord: (id, d) => api.post(`/pipeline/records/${id}/move`, d),
  getRecordHistory: (id) => api.get(`/pipeline/records/${id}/history`),
  convertToEstimate: (id) => api.post(`/pipeline/records/${id}/convert-to-estimate`),
  getAnalytics: (params) => api.get('/pipeline/analytics', { params }),
};

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------
function agingLabel(enteredStageAt) {
  if (!enteredStageAt) return null;
  const diff = Date.now() - new Date(enteredStageAt).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m in stage`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h in stage`;
  const days = Math.floor(hrs / 24);
  return `${days}d in stage`;
}

function formatCurrency(v) {
  if (v == null) return null;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v);
}

// ---------------------------------------------------------------------------
// PipelineCard
// ---------------------------------------------------------------------------
const PipelineCard = React.memo(({ record, index, onClick }) => {
  const aging = agingLabel(record.entered_stage_at);
  const isStale = record.is_stale;
  const overdueLabel = isStale && record.hours_overdue > 0
    ? (record.hours_overdue < 24 ? `${Math.round(record.hours_overdue)}h overdue` : `${Math.floor(record.hours_overdue / 24)}d overdue`)
    : null;

  return (
    <Draggable draggableId={record.id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          data-testid={`pipeline-card-${record.id}`}
          onClick={() => onClick(record)}
          className={`group rounded-lg border bg-background p-3 mb-2 cursor-pointer transition-all
            ${isStale ? 'border-l-[3px] border-l-amber-500' : ''}
            ${snapshot.isDragging ? 'shadow-lg ring-2 ring-primary/30' : 'shadow-sm hover:shadow-md hover:-translate-y-0.5'}
          `}
        >
          <div className="flex items-start justify-between gap-2 mb-1.5">
            <h4 className="text-sm font-semibold text-foreground leading-tight line-clamp-2">{record.title}</h4>
            {record.estimated_value != null && (
              <span className="shrink-0 text-xs font-semibold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">
                {formatCurrency(record.estimated_value)}
              </span>
            )}
          </div>
          {(record.contact_name || record.company_name) && (
            <p className="text-xs text-muted-foreground truncate mb-1">
              {record.contact_name}{record.contact_name && record.company_name ? ' · ' : ''}{record.company_name}
            </p>
          )}
          <div className="flex items-center gap-2 flex-wrap mt-2">
            {record.lead_source && (
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0 font-normal">{record.lead_source}</Badge>
            )}
            {record.converted_estimate_id && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 font-normal text-emerald-600 border-emerald-300 bg-emerald-50/50" data-testid={`converted-badge-${record.id}`}>
                <FileText className="w-2.5 h-2.5 mr-0.5" />Converted
              </Badge>
            )}
            {isStale && overdueLabel && (
              <span className="flex items-center gap-0.5 text-[10px] font-medium text-amber-600" data-testid={`stale-badge-${record.id}`}>
                <AlertTriangle className="w-3 h-3" />{overdueLabel}
              </span>
            )}
            {record.assigned_user_name && (
              <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
                <UserIcon className="w-3 h-3" />{record.assigned_user_name.split(' ')[0]}
              </span>
            )}
            {aging && !isStale && (
              <span className="flex items-center gap-1 text-[10px] text-muted-foreground ml-auto">
                <Clock className="w-3 h-3" />{aging}
              </span>
            )}
          </div>
        </div>
      )}
    </Draggable>
  );
});

// ---------------------------------------------------------------------------
// StageColumn
// ---------------------------------------------------------------------------
const StageColumn = React.memo(({ stage, onCardClick }) => {
  const stageColor = stage.color || '#94a3b8';
  return (
    <div
      className="flex flex-col min-w-[260px] md:min-w-[280px] max-w-[320px] w-[85vw] md:w-[300px] shrink-0 rounded-xl border bg-background shadow-sm"
      data-testid={`stage-column-${stage.id}`}
    >
      <div className="flex items-center gap-2 px-3 py-2.5 rounded-t-xl border-b bg-muted/30" style={{ borderTop: `3px solid ${stageColor}` }}>
        <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: stageColor }} />
        <h3 className="text-sm font-semibold text-foreground truncate flex-1">{stage.name}</h3>
        <span className="text-xs text-muted-foreground font-medium bg-muted rounded-full px-2 py-0.5">
          {stage.record_count}
        </span>
      </div>
      <Droppable droppableId={stage.id}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={`flex-1 p-2 overflow-y-auto min-h-[120px] transition-colors rounded-b-xl
              ${snapshot.isDraggingOver ? 'bg-primary/5' : 'bg-muted/10'}
            `}
            style={{ maxHeight: 'calc(100vh - 300px)' }}
          >
            {stage.records.map((rec, idx) => (
              <PipelineCard key={rec.id} record={rec} index={idx} onClick={onCardClick} />
            ))}
            {provided.placeholder}
            {stage.records.length === 0 && !snapshot.isDraggingOver && (
              <div className="flex items-center justify-center h-20 text-xs text-muted-foreground">
                No leads in this stage
              </div>
            )}
          </div>
        )}
      </Droppable>
    </div>
  );
});

// ---------------------------------------------------------------------------
// RecordDrawer
// ---------------------------------------------------------------------------
const RecordDrawer = ({ open, onClose, record, stages, teamMembers, onSaved }) => {
  const isEdit = !!record;
  const [form, setForm] = useState({
    title: '', stage_id: '', contact_name: '', company_name: '',
    phone: '', email: '', assigned_user_id: '', estimated_value: '',
    lead_source: '', notes: '', lost_reason: '',
  });
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [converting, setConverting] = useState(false);
  const [conversionResult, setConversionResult] = useState(null); // {estimate_id, estimate_number}

  useEffect(() => {
    if (open && record) {
      setForm({
        title: record.title || '',
        stage_id: record.stage_id || '',
        contact_name: record.contact_name || '',
        company_name: record.company_name || '',
        phone: record.phone || '',
        email: record.email || '',
        assigned_user_id: record.assigned_user_id || '',
        estimated_value: record.estimated_value != null ? String(record.estimated_value) : '',
        lead_source: record.lead_source || '',
        notes: record.notes || '',
        lost_reason: record.lost_reason || '',
      });
      setConversionResult(null);
      setLoadingHistory(true);
      pipelineAPI.getRecordHistory(record.id)
        .then(res => setHistory(res.data))
        .catch(() => {})
        .finally(() => setLoadingHistory(false));
    } else if (open && !record) {
      const firstOpen = stages.find(s => s.stage_type === 'open');
      setForm({
        title: '', stage_id: firstOpen?.id || stages[0]?.id || '',
        contact_name: '', company_name: '', phone: '', email: '',
        assigned_user_id: '', estimated_value: '', lead_source: '', notes: '', lost_reason: '',
      });
      setHistory([]);
      setConversionResult(null);
    }
  }, [open, record, stages]);

  const handleConvert = async () => {
    setConverting(true);
    try {
      const res = await pipelineAPI.convertToEstimate(record.id);
      setConversionResult({ estimate_id: res.data.estimate_id, estimate_number: res.data.estimate_number });
      toast.success(`Converted to ${res.data.estimate_number}`);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Conversion failed');
    } finally {
      setConverting(false);
    }
  };

  const isAlreadyConverted = !!record?.converted_estimate_id || !!conversionResult;

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    if (!form.stage_id) { toast.error('Stage is required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        estimated_value: form.estimated_value ? parseFloat(form.estimated_value) : null,
        assigned_user_id: form.assigned_user_id || null,
      };
      if (isEdit) {
        await pipelineAPI.updateRecord(record.id, payload);
        toast.success('Record updated');
      } else {
        await pipelineAPI.createRecord(payload);
        toast.success('Lead created');
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppDrawer
      open={open}
      onOpenChange={(v) => { if (!v) onClose(); }}
      title={isEdit ? 'Edit Lead' : 'Add Lead'}
      description={isEdit ? record?.title : 'Create a new pipeline lead'}
      size="lg"
      data-testid="record-drawer"
      footer={
        <div className="flex justify-end gap-2 p-4 border-t bg-background">
          <Button variant="outline" onClick={onClose} data-testid="record-drawer-cancel">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="record-drawer-save">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {isEdit ? 'Save Changes' : 'Create Lead'}
          </Button>
        </div>
      }
    >
      <div className="p-4 space-y-4 overflow-y-auto">
        <div>
          <Label htmlFor="rec-title">Title *</Label>
          <Input id="rec-title" value={form.title} onChange={e => setField('title', e.target.value)} placeholder="e.g. Kitchen Remodel - Smith" data-testid="record-field-title" />
        </div>
        <div>
          <Label>Stage</Label>
          <Select value={form.stage_id} onValueChange={v => setField('stage_id', v)}>
            <SelectTrigger data-testid="record-field-stage"><SelectValue placeholder="Select stage" /></SelectTrigger>
            <SelectContent>
              {stages.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Contact Name</Label>
            <Input value={form.contact_name} onChange={e => setField('contact_name', e.target.value)} placeholder="John Smith" data-testid="record-field-contact" />
          </div>
          <div>
            <Label>Company</Label>
            <Input value={form.company_name} onChange={e => setField('company_name', e.target.value)} placeholder="Smith Corp" data-testid="record-field-company" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Phone</Label>
            <Input value={form.phone} onChange={e => setField('phone', e.target.value)} placeholder="(555) 123-4567" data-testid="record-field-phone" />
          </div>
          <div>
            <Label>Email</Label>
            <Input value={form.email} onChange={e => setField('email', e.target.value)} placeholder="john@smith.com" data-testid="record-field-email" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Assigned To</Label>
            <Select value={form.assigned_user_id} onValueChange={v => setField('assigned_user_id', v === '__none__' ? '' : v)}>
              <SelectTrigger data-testid="record-field-assigned"><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Unassigned</SelectItem>
                {teamMembers.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Estimated Value</Label>
            <Input type="number" min="0" step="0.01" value={form.estimated_value} onChange={e => setField('estimated_value', e.target.value)} placeholder="5000" data-testid="record-field-value" />
          </div>
        </div>
        <div>
          <Label>Lead Source</Label>
          <Input value={form.lead_source} onChange={e => setField('lead_source', e.target.value)} placeholder="Website, Referral, Google Ads..." data-testid="record-field-lead-source" />
        </div>
        <div>
          <Label>Notes</Label>
          <Textarea value={form.notes} onChange={e => setField('notes', e.target.value)} rows={3} placeholder="Additional details..." data-testid="record-field-notes" />
        </div>
        {isEdit && record?.closed_outcome === 'lost' && (
          <div>
            <Label>Lost Reason</Label>
            <Input value={form.lost_reason} onChange={e => setField('lost_reason', e.target.value)} placeholder="Reason for losing this lead..." data-testid="record-field-lost-reason" />
          </div>
        )}

        {/* Convert to Estimate */}
        {isEdit && (
          <>
            <Separator className="my-4" />
            <div data-testid="convert-to-estimate-section">
              <h4 className="text-sm font-semibold mb-2">Estimate Conversion</h4>
              {isAlreadyConverted ? (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800" data-testid="conversion-done-banner">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-emerald-800 dark:text-emerald-200">
                      Converted to {conversionResult?.estimate_number || 'Estimate'}
                    </p>
                    <a
                      href={`/estimates?id=${conversionResult?.estimate_id || record.converted_estimate_id}`}
                      className="text-xs text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 hover:underline inline-flex items-center gap-1"
                      data-testid="view-estimate-link"
                    >
                      <ExternalLink className="w-3 h-3" /> View Estimate
                    </a>
                  </div>
                </div>
              ) : (
                <Button
                  variant="outline"
                  className="w-full justify-center gap-2 border-blue-200 text-blue-700 hover:bg-blue-50 dark:border-blue-800 dark:text-blue-300 dark:hover:bg-blue-950/30"
                  onClick={handleConvert}
                  disabled={converting}
                  data-testid="convert-to-estimate-btn"
                >
                  {converting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                  {converting ? 'Converting...' : 'Convert to Estimate'}
                </Button>
              )}
            </div>
          </>
        )}
        {isEdit && (
          <>
            <Separator className="my-4" />
            <div>
              <h4 className="text-sm font-semibold mb-2">Stage History</h4>
              {loadingHistory ? (
                <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
                  <Loader2 className="w-3 h-3 animate-spin" /> Loading...
                </div>
              ) : history.length === 0 ? (
                <p className="text-xs text-muted-foreground">No stage changes recorded.</p>
              ) : (
                <div className="space-y-2" data-testid="record-stage-history">
                  {history.map(h => (
                    <div key={h.id} className="flex items-start gap-2 text-xs">
                      <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground mt-1.5 shrink-0" />
                      <div>
                        <span className="text-muted-foreground">
                          {h.from_stage_name ? (
                            <>{h.from_stage_name} <ArrowRight className="w-3 h-3 inline" /> {h.to_stage_name}</>
                          ) : (
                            <>Created in {h.to_stage_name}</>
                          )}
                        </span>
                        <span className="text-muted-foreground/70 ml-2">
                          by {h.changed_by_name} · {new Date(h.changed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </AppDrawer>
  );
};

// ---------------------------------------------------------------------------
// StageManagementDrawer
// ---------------------------------------------------------------------------
const StageManagementDrawer = ({ open, onClose, stages, onUpdated }) => {
  const [allStages, setAllStages] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [newStageName, setNewStageName] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await pipelineAPI.getStages({ include_inactive: true });
        setAllStages(data.sort((a, b) => a.position - b.position));
      } catch {
        setAllStages([...stages].sort((a, b) => a.position - b.position));
      }
    })();
  }, [open, stages]);

  const activeStages = useMemo(() => allStages.filter(s => s.is_active), [allStages]);
  const archivedStages = useMemo(() => allStages.filter(s => !s.is_active), [allStages]);

  const refreshStages = async () => {
    try {
      const { data } = await pipelineAPI.getStages({ include_inactive: true });
      setAllStages(data.sort((a, b) => a.position - b.position));
    } catch { /* keep current */ }
    onUpdated();
  };

  const handleRename = async (stage) => {
    if (!editName.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      await pipelineAPI.updateStage(stage.id, { name: editName.trim() });
      setEditingId(null);
      refreshStages();
      toast.success('Stage renamed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to rename');
    } finally { setSaving(false); }
  };

  const handleTypeChange = async (stage, newType) => {
    setSaving(true);
    try {
      await pipelineAPI.updateStage(stage.id, { stage_type: newType });
      refreshStages();
      toast.success('Stage type updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update type');
    } finally { setSaving(false); }
  };

  const handleMove = async (idx, direction) => {
    const reordered = [...activeStages];
    const swapIdx = idx + direction;
    if (swapIdx < 0 || swapIdx >= reordered.length) return;
    [reordered[idx], reordered[swapIdx]] = [reordered[swapIdx], reordered[idx]];
    const updated = reordered.map((s, i) => ({ ...s, position: i }));
    setAllStages([...updated, ...archivedStages]);
    try {
      await pipelineAPI.reorderStages({ stages: updated.map(s => ({ id: s.id, position: s.position })) });
      refreshStages();
    } catch { toast.error('Failed to reorder'); }
  };

  const handleArchive = async (stage) => {
    if (!window.confirm(`Archive "${stage.name}"? It will be hidden from the board.`)) return;
    setSaving(true);
    try {
      await pipelineAPI.archiveStage(stage.id);
      refreshStages();
      toast.success(`Stage "${stage.name}" archived`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to archive');
    } finally { setSaving(false); }
  };

  const handleRestore = async (stage) => {
    setSaving(true);
    try {
      await pipelineAPI.restoreStage(stage.id);
      refreshStages();
      toast.success(`Stage "${stage.name}" restored`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to restore');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await pipelineAPI.deleteStage(deleteTarget.id);
      setDeleteTarget(null);
      refreshStages();
      toast.success(`Stage "${deleteTarget.name}" permanently deleted`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete stage');
    } finally { setSaving(false); }
  };

  const handleAddStage = async () => {
    if (!newStageName.trim()) return;
    setSaving(true);
    try {
      await pipelineAPI.createStage({ name: newStageName.trim(), stage_type: 'open' });
      setNewStageName('');
      refreshStages();
      toast.success('Stage added');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add stage');
    } finally { setSaving(false); }
  };

  const typeColors = { open: 'bg-blue-100 text-blue-800', won: 'bg-green-100 text-green-800', lost: 'bg-gray-100 text-gray-800' };

  const handleStaleChange = async (stage, value) => {
    const hours = parseInt(value, 10);
    try {
      await pipelineAPI.updateStage(stage.id, { stale_after_hours: isNaN(hours) || hours <= 0 ? 0 : hours });
      refreshStages();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update threshold');
    }
  };

  return (
    <AppDrawer
      open={open}
      onOpenChange={(v) => { if (!v) onClose(); }}
      title="Manage Stages"
      description="Rename, reorder, archive, or delete pipeline stages."
      size="md"
      data-testid="stage-management-drawer"
    >
      <div className="space-y-3 overflow-y-auto">
        {activeStages.map((stage, idx) => (
          <div key={stage.id} className="flex items-center gap-2 p-2.5 rounded-lg border bg-background" data-testid={`stage-manage-${stage.id}`}>
            <div className="flex flex-col gap-0.5 shrink-0">
              <button onClick={() => handleMove(idx, -1)} disabled={idx === 0} className="p-0.5 rounded hover:bg-muted disabled:opacity-30" data-testid={`stage-move-up-${stage.id}`}>
                <ChevronUp className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => handleMove(idx, 1)} disabled={idx === activeStages.length - 1} className="p-0.5 rounded hover:bg-muted disabled:opacity-30" data-testid={`stage-move-down-${stage.id}`}>
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: stage.color || '#94a3b8' }} />
            <div className="flex-1 min-w-0">
              {editingId === stage.id ? (
                <div className="flex gap-1">
                  <Input
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleRename(stage); if (e.key === 'Escape') setEditingId(null); }}
                    className="h-7 text-sm"
                    autoFocus
                    data-testid={`stage-rename-input-${stage.id}`}
                  />
                  <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => handleRename(stage)} disabled={saving}>Save</Button>
                  <Button size="sm" variant="ghost" className="h-7 px-1" onClick={() => setEditingId(null)}><X className="w-3.5 h-3.5" /></Button>
                </div>
              ) : (
                <button
                  className="text-sm font-medium hover:underline text-left truncate block w-full"
                  onClick={() => { setEditingId(stage.id); setEditName(stage.name); }}
                  data-testid={`stage-name-${stage.id}`}
                >
                  {stage.name}
                </button>
              )}
            </div>
            <Select value={stage.stage_type} onValueChange={v => handleTypeChange(stage, v)}>
              <SelectTrigger className={`h-7 w-[80px] text-[11px] font-medium ${typeColors[stage.stage_type] || ''}`} data-testid={`stage-type-${stage.id}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="won">Won</SelectItem>
                <SelectItem value="lost">Lost</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex items-center gap-0.5 shrink-0" title="Mark leads overdue after this many hours (0 = disabled)">
              <Clock className="w-3 h-3 text-muted-foreground" />
              <input
                type="number"
                min="0"
                placeholder="—"
                value={stage.stale_after_hours || ''}
                onChange={e => handleStaleChange(stage, e.target.value)}
                className="w-10 h-6 text-[11px] text-center border rounded bg-background px-0.5 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                data-testid={`stage-stale-input-${stage.id}`}
              />
              <span className="text-[10px] text-muted-foreground">h</span>
            </div>
            <button
              onClick={() => handleArchive(stage)}
              className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              title="Archive stage"
              data-testid={`stage-archive-${stage.id}`}
            >
              <Archive className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => setDeleteTarget(stage)}
              className="p-1.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
              title="Delete stage permanently"
              data-testid={`stage-delete-${stage.id}`}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        <Separator />
        <div className="flex gap-2">
          <Input
            value={newStageName}
            onChange={e => setNewStageName(e.target.value)}
            placeholder="New stage name..."
            onKeyDown={e => { if (e.key === 'Enter') handleAddStage(); }}
            className="h-9"
            data-testid="stage-add-input"
          />
          <Button onClick={handleAddStage} disabled={saving || !newStageName.trim()} size="sm" className="h-9 px-3" data-testid="stage-add-btn">
            <Plus className="w-4 h-4 mr-1" /> Add
          </Button>
        </div>

        {/* Archived stages toggle + section */}
        {archivedStages.length > 0 && (
          <>
            <button
              onClick={() => setShowArchived(prev => !prev)}
              className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors w-full pt-1"
              data-testid="toggle-archived-stages"
            >
              <Archive className="w-3.5 h-3.5" />
              <span>{showArchived ? 'Hide' : 'Show'} archived stages ({archivedStages.length})</span>
              <ChevronDown className={`w-3.5 h-3.5 ml-auto transition-transform ${showArchived ? 'rotate-180' : ''}`} />
            </button>

            {showArchived && (
              <div className="space-y-2 pt-1" data-testid="archived-stages-section">
                {archivedStages.map(stage => (
                  <div key={stage.id} className="flex items-center gap-2 p-2.5 rounded-lg border border-dashed bg-muted/30 opacity-70" data-testid={`stage-archived-${stage.id}`}>
                    <div className="w-3 h-3 rounded-full shrink-0 opacity-50" style={{ backgroundColor: stage.color || '#94a3b8' }} />
                    <span className="flex-1 text-sm text-muted-foreground truncate">{stage.name}</span>
                    <Badge variant="outline" className="text-[10px] h-5 font-normal text-muted-foreground">{stage.stage_type}</Badge>
                    <button
                      onClick={() => handleRestore(stage)}
                      className="p-1.5 rounded hover:bg-primary/10 text-muted-foreground hover:text-primary transition-colors"
                      title="Restore stage"
                      data-testid={`stage-restore-${stage.id}`}
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => setDeleteTarget(stage)}
                      className="p-1.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
                      title="Delete permanently"
                      data-testid={`stage-delete-${stage.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}>
        <AlertDialogContent data-testid="stage-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete stage permanently?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the <span className="font-semibold text-foreground">"{deleteTarget?.name}"</span> stage. This cannot be undone.
              {deleteTarget?.stage_type !== 'open' && (
                <span className="block mt-2 text-xs text-muted-foreground">
                  Note: Every pipeline requires at least one open, one won, and one lost stage. If this is the last stage of its type, deletion will be blocked.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="stage-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={saving}
              data-testid="stage-delete-confirm-btn"
            >
              {saving ? 'Deleting...' : 'Delete permanently'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppDrawer>
  );
};

// ---------------------------------------------------------------------------
// FilterDrawer — right-side panel for pipeline filters
// ---------------------------------------------------------------------------
const FilterDrawer = ({ open, onClose, filters, setFilters, stages, teamMembers, leadSources }) => (
  <AppDrawer
    open={open}
    onOpenChange={(v) => { if (!v) onClose(); }}
    title="Filters"
    description="Narrow down your pipeline view."
    size="md"
    data-testid="filter-drawer"
  >
    <div className="space-y-5">
      <div>
        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Assigned Rep</Label>
        <Select value={filters.assigned || '__all__'} onValueChange={v => setFilters(p => ({ ...p, assigned: v === '__all__' ? '' : v }))}>
          <SelectTrigger className="mt-1.5" data-testid="filter-assigned">
            <SelectValue placeholder="All reps" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All reps</SelectItem>
            {teamMembers.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Stage</Label>
        <Select value={filters.stage || '__all__'} onValueChange={v => setFilters(p => ({ ...p, stage: v === '__all__' ? '' : v }))}>
          <SelectTrigger className="mt-1.5" data-testid="filter-stage">
            <SelectValue placeholder="All stages" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All stages</SelectItem>
            {stages.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Lead Source</Label>
        <Select value={filters.source || '__all__'} onValueChange={v => setFilters(p => ({ ...p, source: v === '__all__' ? '' : v }))}>
          <SelectTrigger className="mt-1.5" data-testid="filter-source">
            <SelectValue placeholder="All sources" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All sources</SelectItem>
            {leadSources.map(ls => <SelectItem key={ls} value={ls}>{ls}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Status</Label>
        <Select value={filters.showClosed ? 'all' : 'open'} onValueChange={v => setFilters(p => ({ ...p, showClosed: v === 'all' }))}>
          <SelectTrigger className="mt-1.5" data-testid="filter-status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="open">Open only</SelectItem>
            <SelectItem value="all">Open + Closed</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <Separator />
      <Button
        variant="ghost"
        size="sm"
        className="w-full text-muted-foreground"
        onClick={() => setFilters({ assigned: '', stage: '', source: '', showClosed: false })}
        data-testid="filter-clear-all"
      >
        Clear all filters
      </Button>
    </div>
  </AppDrawer>
);

// ---------------------------------------------------------------------------
// TableView — sortable flat table
// ---------------------------------------------------------------------------
const TableView = React.memo(({ records, stages, onRowClick }) => {
  const [sortKey, setSortKey] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const stageMap = useMemo(() => {
    const m = {};
    stages.forEach(s => { m[s.id] = s; });
    return m;
  }, [stages]);

  const sorted = useMemo(() => {
    const list = [...records];
    list.sort((a, b) => {
      let av = a[sortKey], bv = b[sortKey];
      if (sortKey === 'stage_id') { av = stageMap[av]?.name || ''; bv = stageMap[bv]?.name || ''; }
      if (av == null) av = '';
      if (bv == null) bv = '';
      if (typeof av === 'string') { av = av.toLowerCase(); bv = bv.toLowerCase(); }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [records, sortKey, sortDir, stageMap]);

  if (records.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-2 text-muted-foreground">
        <AlertCircle className="w-8 h-8" />
        <p className="text-sm">No pipeline records match your filters.</p>
      </div>
    );
  }

  return (
    <div className="overflow-auto flex-1" data-testid="pipeline-table">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/30">
            <SortableTableHead sortKey={sortKey} sortDir={sortDir} columnKey="title" onSort={handleSort}>Title</SortableTableHead>
            <SortableTableHead sortKey={sortKey} sortDir={sortDir} columnKey="contact_name" onSort={handleSort}>Contact</SortableTableHead>
            <SortableTableHead sortKey={sortKey} sortDir={sortDir} columnKey="company_name" onSort={handleSort}>Company</SortableTableHead>
            <SortableTableHead sortKey={sortKey} sortDir={sortDir} columnKey="stage_id" onSort={handleSort}>Stage</SortableTableHead>
            <SortableTableHead sortKey={sortKey} sortDir={sortDir} columnKey="estimated_value" onSort={handleSort} className="text-right">Value</SortableTableHead>
            <SortableTableHead sortKey={sortKey} sortDir={sortDir} columnKey="lead_source" onSort={handleSort}>Source</SortableTableHead>
            <SortableTableHead sortKey={sortKey} sortDir={sortDir} columnKey="assigned_user_name" onSort={handleSort}>Assigned</SortableTableHead>
            <SortableTableHead sortKey={sortKey} sortDir={sortDir} columnKey="entered_stage_at" onSort={handleSort}>In Stage</SortableTableHead>
            <SortableTableHead sortKey={sortKey} sortDir={sortDir} columnKey="created_at" onSort={handleSort}>Created</SortableTableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.map(rec => {
            const stage = stageMap[rec.stage_id];
            return (
              <TableRow key={rec.id} className="cursor-pointer hover:bg-muted/40 transition-colors" onClick={() => onRowClick(rec)} data-testid={`pipeline-table-row-${rec.id}`}>
                <TableCell className="font-medium max-w-[220px] truncate">{rec.title}</TableCell>
                <TableCell className="text-muted-foreground max-w-[150px] truncate">{rec.contact_name || '—'}</TableCell>
                <TableCell className="text-muted-foreground max-w-[150px] truncate">{rec.company_name || '—'}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: stage?.color || '#94a3b8' }} />
                    <span className="text-sm truncate">{stage?.name || '—'}</span>
                  </div>
                </TableCell>
                <TableCell className="text-right font-medium tabular-nums">{rec.estimated_value != null ? formatCurrency(rec.estimated_value) : '—'}</TableCell>
                <TableCell>{rec.lead_source ? <Badge variant="secondary" className="text-[10px] px-1.5 py-0 font-normal">{rec.lead_source}</Badge> : '—'}</TableCell>
                <TableCell className="text-muted-foreground text-sm">{rec.assigned_user_name || '—'}</TableCell>
                <TableCell className={`text-xs ${rec.is_stale ? 'text-amber-600 font-medium' : 'text-muted-foreground'}`}>
                  {rec.is_stale && <AlertTriangle className="w-3 h-3 inline mr-1" />}
                  {agingLabel(rec.entered_stage_at) || '—'}
                </TableCell>
                <TableCell className="text-muted-foreground text-xs whitespace-nowrap">{rec.created_at ? new Date(rec.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
});

// ---------------------------------------------------------------------------
// AnalyticsView — comprehensive pipeline metrics & breakdowns
// ---------------------------------------------------------------------------
function formatDuration(hours) {
  if (hours == null || hours === 0) return '—';
  if (hours < 1) return `${Math.round(hours * 60)}m`;
  if (hours < 24) return `${Math.round(hours)}h`;
  const days = Math.floor(hours / 24);
  const rem = Math.round(hours % 24);
  return rem > 0 ? `${days}d ${rem}h` : `${days}d`;
}

const PERIOD_OPTIONS = [
  { value: 'all', label: 'All Time' },
  { value: '90d', label: 'Last 90 Days' },
  { value: '30d', label: 'Last 30 Days' },
];

const AnalyticsView = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('all');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    pipelineAPI.getAnalytics({ period })
      .then(res => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) toast.error('Failed to load analytics'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [period]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!data) return null;

  const s = data.summary;

  // Find max avg_hours for bar chart scaling
  const maxStageHours = Math.max(...data.avg_time_in_stage.map(st => st.avg_hours), 1);

  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-6" data-testid="analytics-view">
      {/* Period selector */}
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-foreground">Pipeline Analytics</h2>
        <div className="flex items-center gap-1.5 rounded-md border bg-muted/30 p-0.5" data-testid="period-selector">
          {PERIOD_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => setPeriod(opt.value)}
              className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                period === opt.value
                  ? 'bg-background shadow-sm text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid={`period-${opt.value}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* KPI summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="analytics-summary">
        {[
          { label: 'Conversion Rate', value: `${s.conversion_rate}%`, sub: `${s.won_count} won / ${s.won_count + s.lost_count} decided`, icon: TrendingUp, accent: 'emerald' },
          { label: 'Won', value: String(s.won_count), sub: formatCurrency(s.won_value), icon: CheckCircle2, accent: 'emerald' },
          { label: 'Open', value: String(s.open_count), sub: formatCurrency(s.open_pipeline_value), icon: Target, accent: 'blue' },
          { label: 'Lost', value: String(s.lost_count), sub: null, icon: X, accent: 'amber' },
          { label: 'Avg Time to Win', value: formatDuration(s.avg_time_to_win_hours), sub: `${s.stale_count} stale`, icon: Timer, accent: 'violet' },
        ].map(c => (
          <div key={c.label} className="relative overflow-hidden rounded-lg border bg-background shadow-sm" data-testid={`analytics-kpi-${c.label.toLowerCase().replace(/\s/g, '-')}`}>
            <div className={`absolute left-0 top-0 bottom-0 w-1 ${STAT_ACCENT[c.accent]}`} />
            <div className="p-3 pl-4">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide truncate">{c.label}</p>
                  <p className="text-xl font-bold mt-0.5 tabular-nums text-foreground">{c.value}</p>
                  {c.sub && <p className="text-xs text-muted-foreground mt-0.5">{c.sub}</p>}
                </div>
                <div className="p-2 rounded-lg bg-muted/50 shrink-0">
                  <c.icon className="w-4.5 h-4.5 text-muted-foreground" />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Avg Time in Stage — horizontal bar chart */}
      <div className="rounded-lg border bg-background shadow-sm overflow-hidden" data-testid="avg-time-in-stage">
        <div className="p-4 border-b">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Clock className="w-4 h-4 text-muted-foreground" /> Average Time in Stage
          </h3>
        </div>
        <div className="p-4 space-y-3">
          {data.avg_time_in_stage.filter(st => st.stage_type === 'open').map(st => (
            <div key={st.stage_id} className="flex items-center gap-3" data-testid={`stage-bar-${st.stage_id}`}>
              <span className="text-xs font-medium text-foreground w-28 truncate shrink-0">{st.stage_name}</span>
              <div className="flex-1 h-6 bg-muted/30 rounded overflow-hidden relative">
                <div
                  className="h-full rounded transition-all duration-500"
                  style={{
                    width: `${Math.max((st.avg_hours / maxStageHours) * 100, st.avg_hours > 0 ? 4 : 0)}%`,
                    backgroundColor: st.color || '#6366f1',
                    opacity: 0.8,
                  }}
                />
                {st.avg_hours > 0 && (
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] font-medium text-foreground">
                    {formatDuration(st.avg_hours)}
                  </span>
                )}
              </div>
              <span className="text-[10px] text-muted-foreground w-16 text-right shrink-0">{st.sample_count} leads</span>
            </div>
          ))}
          {data.avg_time_in_stage.filter(st => st.stage_type === 'open').length === 0 && (
            <p className="text-xs text-muted-foreground text-center py-4">No stage data available</p>
          )}
        </div>
      </div>

      {/* Lead Source Breakdown */}
      <div className="rounded-lg border bg-background shadow-sm overflow-hidden" data-testid="lead-source-breakdown">
        <div className="p-4 border-b">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-muted-foreground" /> Lead Source Breakdown
          </h3>
        </div>
        {data.lead_source_breakdown.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-8">No lead data available</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="lead-source-table">
              <thead>
                <tr className="border-b bg-muted/20">
                  <th className="text-left p-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Source</th>
                  <th className="text-right p-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Leads</th>
                  <th className="text-right p-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Won</th>
                  <th className="text-right p-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Lost</th>
                  <th className="text-right p-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Open</th>
                  <th className="text-right p-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Conv. Rate</th>
                  <th className="text-right p-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Total Value</th>
                  <th className="text-right p-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Won Value</th>
                </tr>
              </thead>
              <tbody>
                {data.lead_source_breakdown.map(ls => (
                  <tr key={ls.source} className="border-b last:border-0 hover:bg-muted/10" data-testid={`source-row-${ls.source}`}>
                    <td className="p-3 font-medium text-foreground">{ls.source}</td>
                    <td className="p-3 text-right tabular-nums">{ls.total}</td>
                    <td className="p-3 text-right tabular-nums text-emerald-600 font-medium">{ls.won}</td>
                    <td className="p-3 text-right tabular-nums text-red-500">{ls.lost}</td>
                    <td className="p-3 text-right tabular-nums text-blue-600">{ls.open}</td>
                    <td className="p-3 text-right tabular-nums font-medium">{ls.conversion_rate}%</td>
                    <td className="p-3 text-right tabular-nums">{formatCurrency(ls.total_value)}</td>
                    <td className="p-3 text-right tabular-nums text-emerald-600">{formatCurrency(ls.won_value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Rep Performance */}
      <div className="rounded-lg border bg-background shadow-sm overflow-hidden" data-testid="rep-performance">
        <div className="p-4 border-b">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Users className="w-4 h-4 text-muted-foreground" /> Rep Performance
          </h3>
        </div>
        {data.rep_performance.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-8">No rep data available</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="rep-performance-table">
              <thead>
                <tr className="border-b bg-muted/20">
                  <th className="text-left p-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Rep</th>
                  <th className="text-right p-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Leads</th>
                  <th className="text-right p-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Won</th>
                  <th className="text-right p-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Lost</th>
                  <th className="text-right p-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Open</th>
                  <th className="text-right p-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Conv. Rate</th>
                  <th className="text-right p-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Won Value</th>
                  <th className="text-right p-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Avg Time to Win</th>
                </tr>
              </thead>
              <tbody>
                {data.rep_performance.map(r => (
                  <tr key={r.user_id || 'unassigned'} className="border-b last:border-0 hover:bg-muted/10" data-testid={`rep-row-${r.user_id || 'unassigned'}`}>
                    <td className="p-3 font-medium text-foreground">
                      <span className={r.user_id ? '' : 'text-muted-foreground italic'}>{r.name}</span>
                    </td>
                    <td className="p-3 text-right tabular-nums">{r.total}</td>
                    <td className="p-3 text-right tabular-nums text-emerald-600 font-medium">{r.won}</td>
                    <td className="p-3 text-right tabular-nums text-red-500">{r.lost}</td>
                    <td className="p-3 text-right tabular-nums text-blue-600">{r.open}</td>
                    <td className="p-3 text-right tabular-nums font-medium">{r.conversion_rate}%</td>
                    <td className="p-3 text-right tabular-nums text-emerald-600">{formatCurrency(r.won_value)}</td>
                    <td className="p-3 text-right tabular-nums">{formatDuration(r.avg_time_to_win_hours)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// SummaryCards — pipeline metrics (Dashboard KPI card style)
// ---------------------------------------------------------------------------
const STAT_ACCENT = { emerald: 'bg-emerald-500', blue: 'bg-blue-500', amber: 'bg-amber-500', violet: 'bg-violet-500' };

const SummaryCards = ({ stats }) => {
  if (!stats) return null;

  const formatAge = (hours) => {
    if (hours < 1) return '<1h';
    if (hours < 24) return `${Math.round(hours)}h`;
    const days = Math.floor(hours / 24);
    const rem = Math.round(hours % 24);
    return rem > 0 ? `${days}d ${rem}h` : `${days}d`;
  };

  const cards = [
    { label: 'Pipeline Value', value: formatCurrency(stats.total_pipeline_value) || '$0', icon: DollarSign, accent: 'emerald', testId: 'stat-pipeline-value' },
    { label: 'Open Leads', value: stats.stale_count > 0 ? `${stats.open_count} (${stats.stale_count} overdue)` : String(stats.open_count), icon: Target, accent: stats.stale_count > 0 ? 'amber' : 'blue', testId: 'stat-open-leads' },
    { label: 'Won', value: `${stats.won_count}${stats.conversion_rate > 0 ? ` (${stats.conversion_rate}%)` : ''}`, icon: TrendingUp, accent: 'amber', testId: 'stat-won' },
    { label: 'Avg. Time in Stage', value: formatAge(stats.avg_age_hours), icon: Timer, accent: 'violet', testId: 'stat-avg-age' },
  ];

  return (
    <div className="border-b bg-muted/20 shrink-0 px-4 py-3" data-testid="pipeline-summary">
      {/* Mobile: horizontal scroll row | Desktop: 4-col grid */}
      <div className="hidden md:grid grid-cols-4 gap-3">
        {cards.map(c => (
          <div key={c.testId} className="relative overflow-hidden rounded-lg border bg-background shadow-sm" data-testid={c.testId}>
            <div className={`absolute left-0 top-0 bottom-0 w-1 ${STAT_ACCENT[c.accent]}`} />
            <div className="p-3 pl-4">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide truncate">{c.label}</p>
                  <p className="text-xl font-bold mt-0.5 tabular-nums text-foreground">{c.value}</p>
                </div>
                <div className="p-2 rounded-lg bg-muted/50 shrink-0">
                  <c.icon className="w-4.5 h-4.5 text-muted-foreground" />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="flex md:hidden gap-2.5 overflow-x-auto pb-1 -mx-4 px-4 snap-x snap-mandatory scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
        {cards.map(c => (
          <div key={c.testId} className="relative overflow-hidden rounded-lg border bg-background shadow-sm min-w-[160px] w-[160px] shrink-0 snap-start" data-testid={c.testId}>
            <div className={`absolute left-0 top-0 bottom-0 w-1 ${STAT_ACCENT[c.accent]}`} />
            <div className="p-3 pl-4">
              <div className="flex items-center gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide truncate">{c.label}</p>
                  <p className="text-lg font-bold mt-0.5 tabular-nums text-foreground leading-tight">{c.value}</p>
                </div>
                <div className="p-1.5 rounded-lg bg-muted/50 shrink-0">
                  <c.icon className="w-3.5 h-3.5 text-muted-foreground" />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main SalesPipeline Page
// ---------------------------------------------------------------------------
const SalesPipeline = () => {
  const { user } = useAuth();
  const [boardData, setBoardData] = useState({ stages: [], total_records: 0, team_members: [] });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ assigned: '', stage: '', source: '', showClosed: false });
  const [recordDrawerOpen, setRecordDrawerOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [stageDrawerOpen, setStageDrawerOpen] = useState(false);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [automationsDrawerOpen, setAutomationsDrawerOpen] = useState(false);
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem('pipeline_view') || 'board'; } catch { return 'board'; }
  });

  const boardRef = useRef(null);
  const [showScrollFade, setShowScrollFade] = useState(false);

  useEffect(() => { try { localStorage.setItem('pipeline_view', viewMode); } catch {} }, [viewMode]);

  const fetchBoard = useCallback(async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (filters.assigned) params.assigned_user_id = filters.assigned;
      if (filters.showClosed) params.show_closed = true;
      const res = await pipelineAPI.getBoard(params);
      setBoardData(res.data);
    } catch (err) {
      toast.error('Failed to load pipeline');
    } finally { setLoading(false); }
  }, [search, filters.assigned, filters.showClosed]);

  const fetchStats = useCallback(async () => {
    try { const res = await pipelineAPI.getStats(); setStats(res.data); } catch {}
  }, []);

  useEffect(() => { fetchBoard(); fetchStats(); }, [fetchBoard, fetchStats]);

  useEffect(() => {
    const el = boardRef.current;
    if (!el || viewMode !== 'board') { setShowScrollFade(false); return; }
    const check = () => {
      const canScroll = el.scrollWidth > el.clientWidth;
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 8;
      setShowScrollFade(canScroll && !atEnd);
    };
    check();
    el.addEventListener('scroll', check, { passive: true });
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => { el.removeEventListener('scroll', check); ro.disconnect(); };
  }, [viewMode, boardData]);

  const [searchInput, setSearchInput] = useState('');
  useEffect(() => { const t = setTimeout(() => setSearch(searchInput), 400); return () => clearTimeout(t); }, [searchInput]);

  const onDragEnd = useCallback(async (result) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;
    setBoardData(prev => {
      const newStages = prev.stages.map(s => ({ ...s, records: [...s.records] }));
      const srcStage = newStages.find(s => s.id === source.droppableId);
      const destStage = newStages.find(s => s.id === destination.droppableId);
      if (!srcStage || !destStage) return prev;
      const [moved] = srcStage.records.splice(source.index, 1);
      moved.stage_id = destination.droppableId;
      moved.entered_stage_at = new Date().toISOString();
      destStage.records.splice(destination.index, 0, moved);
      srcStage.record_count = srcStage.records.length;
      destStage.record_count = destStage.records.length;
      return { ...prev, stages: newStages };
    });
    if (source.droppableId !== destination.droppableId) {
      try {
        await pipelineAPI.moveRecord(draggableId, { stage_id: destination.droppableId });
        fetchStats();
      } catch (err) { toast.error(err.response?.data?.detail || 'Failed to move card'); fetchBoard(); }
    }
  }, [fetchBoard, fetchStats]);

  const handleCardClick = useCallback((record) => { setSelectedRecord(record); setRecordDrawerOpen(true); }, []);
  const handleAddLead = () => { setSelectedRecord(null); setRecordDrawerOpen(true); };
  const handleSaved = () => { fetchBoard(); fetchStats(); };

  const teamMembers = boardData.team_members || [];
  const allStages = boardData.stages || [];
  const leadSources = useMemo(() => {
    const set = new Set();
    allStages.forEach(s => s.records.forEach(r => { if (r.lead_source) set.add(r.lead_source); }));
    return [...set].sort();
  }, [allStages]);

  const activeFilterCount = [filters.assigned, filters.stage, filters.source, filters.showClosed].filter(Boolean).length;

  const filteredStages = useMemo(() => {
    if (!filters.stage && !filters.source) return allStages;
    return allStages
      .filter(s => !filters.stage || s.id === filters.stage)
      .map(s => {
        if (!filters.source) return s;
        const filtered = s.records.filter(r => r.lead_source === filters.source);
        return { ...s, records: filtered, record_count: filtered.length };
      });
  }, [allStages, filters.stage, filters.source]);

  const flatRecords = useMemo(() => {
    let recs = [];
    allStages.forEach(s => s.records.forEach(r => recs.push(r)));
    if (filters.stage) recs = recs.filter(r => r.stage_id === filters.stage);
    if (filters.source) recs = recs.filter(r => r.lead_source === filters.source);
    return recs;
  }, [allStages, filters.stage, filters.source]);

  const displayTotal = viewMode === 'board'
    ? filteredStages.reduce((sum, s) => sum + s.record_count, 0)
    : flatRecords.length;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]" data-testid="pipeline-loading">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex flex-col h-[calc(100vh-64px)] w-full" data-testid="sales-pipeline-page">
        {/* Header — stacked on mobile, single row on desktop */}
        <div className="border-b bg-background shrink-0">
          {/* Title row */}
          <div className="flex items-center gap-3 px-4 pt-4 pb-2 md:pb-3 md:pt-3">
            <div className="mr-auto min-w-0">
              <h1 className="text-xl md:text-lg font-semibold text-foreground" data-testid="pipeline-title">Sales Pipeline</h1>
              <p className="text-xs text-muted-foreground mt-0.5">Track leads from first contact to sold job.</p>
            </div>
            {/* Desktop-only inline controls */}
            <div className="hidden md:flex items-center gap-3">
              <div className="relative w-52">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input placeholder="Search leads..." value={searchInput} onChange={e => setSearchInput(e.target.value)} className="pl-9 h-8 text-sm" data-testid="pipeline-search" />
              </div>
              <Button variant="outline" size="sm" onClick={() => setFilterDrawerOpen(true)} className="relative" data-testid="filter-btn">
                <SlidersHorizontal className="w-4 h-4 mr-1.5" /> Filters
                {activeFilterCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 flex items-center justify-center rounded-full bg-primary text-primary-foreground text-[10px] font-bold leading-none min-w-[18px] h-[18px] px-1" data-testid="filter-count">{activeFilterCount}</span>
                )}
              </Button>
              <div className="flex items-center rounded-md border bg-muted/30 p-0.5" data-testid="view-toggle">
                <button onClick={() => setViewMode('board')} className={`flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-colors ${viewMode === 'board' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`} data-testid="view-toggle-board">
                  <LayoutGrid className="w-3.5 h-3.5" /> Board
                </button>
                <button onClick={() => setViewMode('table')} className={`flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-colors ${viewMode === 'table' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`} data-testid="view-toggle-table">
                  <List className="w-3.5 h-3.5" /> Table
                </button>
                <button onClick={() => setViewMode('analytics')} className={`flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-colors ${viewMode === 'analytics' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`} data-testid="view-toggle-analytics">
                  <BarChart3 className="w-3.5 h-3.5" /> Analytics
                </button>
              </div>
              <Button variant="outline" size="sm" onClick={() => setStageDrawerOpen(true)} data-testid="manage-stages-btn">
                <Settings2 className="w-4 h-4 mr-1.5" /> Manage Stages
              </Button>
              <Button variant="outline" size="sm" onClick={() => setAutomationsDrawerOpen(true)} data-testid="automations-btn">
                <Zap className="w-4 h-4 mr-1.5" /> Automations
              </Button>
              <Button size="sm" onClick={handleAddLead} data-testid="add-lead-btn">
                <Plus className="w-4 h-4 mr-1.5" /> Add Lead
              </Button>
            </div>
          </div>

          {/* Mobile controls row — below title */}
          <div className="flex md:hidden items-center gap-2 px-4 pb-3">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search leads..." value={searchInput} onChange={e => setSearchInput(e.target.value)} className="pl-9 h-9 text-sm w-full" data-testid="pipeline-search-mobile" />
            </div>
            <div className="flex items-center rounded-md border bg-muted/30 p-0.5 shrink-0" data-testid="view-toggle-mobile">
              <button onClick={() => setViewMode('board')} className={`p-1.5 rounded transition-colors ${viewMode === 'board' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground'}`} data-testid="view-toggle-board-mobile">
                <LayoutGrid className="w-4 h-4" />
              </button>
              <button onClick={() => setViewMode('table')} className={`p-1.5 rounded transition-colors ${viewMode === 'table' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground'}`} data-testid="view-toggle-table-mobile">
                <List className="w-4 h-4" />
              </button>
              <button onClick={() => setViewMode('analytics')} className={`p-1.5 rounded transition-colors ${viewMode === 'analytics' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground'}`} data-testid="view-toggle-analytics-mobile">
                <BarChart3 className="w-4 h-4" />
              </button>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="h-9 w-9 shrink-0 relative" data-testid="pipeline-mobile-overflow-menu">
                  <MoreVertical className="w-4 h-4" />
                  {activeFilterCount > 0 && (
                    <span className="absolute -top-1 -right-1 flex items-center justify-center rounded-full bg-primary text-primary-foreground text-[9px] font-bold leading-none w-4 h-4">{activeFilterCount}</span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => setFilterDrawerOpen(true)} data-testid="overflow-filter-btn">
                  <SlidersHorizontal className="w-4 h-4 mr-2" />
                  Filters
                  {activeFilterCount > 0 && (
                    <Badge variant="secondary" className="ml-auto text-[10px] px-1.5 py-0 h-5">{activeFilterCount}</Badge>
                  )}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStageDrawerOpen(true)} data-testid="overflow-manage-stages-btn">
                  <Settings2 className="w-4 h-4 mr-2" />
                  Manage Stages
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setAutomationsDrawerOpen(true)} data-testid="overflow-automations-btn">
                  <Zap className="w-4 h-4 mr-2" />
                  Automations
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Summary cards (board/table only) */}
        {viewMode !== 'analytics' && <SummaryCards stats={stats} />}

        {/* Lead count bar (board/table only) */}
        {viewMode !== 'analytics' && (
          <div className="flex items-center px-4 py-1.5 shrink-0">
            <span className="text-xs text-muted-foreground" data-testid="pipeline-total">
              {displayTotal} lead{displayTotal !== 1 ? 's' : ''}
              {activeFilterCount > 0 && ' (filtered)'}
            </span>
          </div>
        )}

        {/* Board / Table / Analytics */}
        {viewMode === 'analytics' ? (
          <AnalyticsView />
        ) : viewMode === 'board' ? (
          <div className="flex-1 relative overflow-hidden bg-muted/30">
            <div ref={boardRef} className="h-full overflow-x-auto overflow-y-hidden" data-testid="pipeline-board">
              {filteredStages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full gap-3 text-muted-foreground">
                  <AlertCircle className="w-10 h-10" />
                  <p className="text-sm">No pipeline stages found. Add stages to get started.</p>
                  <Button variant="outline" size="sm" onClick={() => setStageDrawerOpen(true)}>Manage Stages</Button>
                </div>
              ) : (
                <DragDropContext onDragEnd={onDragEnd}>
                  <div className="flex gap-3 p-3 md:p-4 h-full items-start snap-x snap-mandatory md:snap-none" data-testid="pipeline-columns">
                    {filteredStages.map(stage => (
                      <StageColumn key={stage.id} stage={stage} onCardClick={handleCardClick} />
                    ))}
                  </div>
                </DragDropContext>
              )}
            </div>
            {showScrollFade && (
              <div className="absolute right-0 top-0 bottom-0 w-12 pointer-events-none" style={{ background: 'linear-gradient(to right, transparent, hsl(var(--background)) 85%)' }} data-testid="board-scroll-fade" />
            )}
          </div>
        ) : (
          <TableView records={flatRecords} stages={allStages} onRowClick={handleCardClick} />
        )}
      </div>

      <RecordDrawer open={recordDrawerOpen} onClose={() => { setRecordDrawerOpen(false); setSelectedRecord(null); }} record={selectedRecord} stages={allStages} teamMembers={teamMembers} onSaved={handleSaved} />
      <StageManagementDrawer open={stageDrawerOpen} onClose={() => setStageDrawerOpen(false)} stages={allStages} onUpdated={handleSaved} />
      <FilterDrawer open={filterDrawerOpen} onClose={() => setFilterDrawerOpen(false)} filters={filters} setFilters={setFilters} stages={allStages} teamMembers={teamMembers} leadSources={leadSources} />
      <AutomationsDrawer open={automationsDrawerOpen} onClose={() => setAutomationsDrawerOpen(false)} stages={allStages} teamMembers={teamMembers} />
      <MobileFAB onClick={handleAddLead} data-testid="mobile-fab-add-lead" />
    </Layout>
  );
};

export default SalesPipeline;
